import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAdmin } from '@/contexts/AdminContext';
import axios from 'axios';
import { ArrowLeft, Save, CreditCard, Banknote, Info } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPayments = () => {
  const { isAdmin, loading: authLoading } = useAdmin();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [events, setEvents] = useState([]);
  const [editingEventId, setEditingEventId] = useState(null);
  const [editingSumupValue, setEditingSumupValue] = useState('');
  const [form, setForm] = useState({
    iban_1_label: '',
    iban_1_number: '',
    iban_2_label: '',
    iban_2_number: '',
    iban_holder: '',
    default_sumup_url: '',
  });

  useEffect(() => {
    if (authLoading) return;
    if (!isAdmin) return;
    Promise.all([
      axios.get(`${API}/settings`),
      axios.get(`${API}/events`),
    ])
      .then(([sRes, eRes]) => {
        const s = sRes.data;
        setForm({
          iban_1_label: s.iban_1_label || '',
          iban_1_number: s.iban_1_number || '',
          iban_2_label: s.iban_2_label || '',
          iban_2_number: s.iban_2_number || '',
          iban_holder: s.iban_holder || '',
          default_sumup_url: s.default_sumup_url || '',
        });
        setEvents(eRes.data || []);
      })
      .catch((e) => {
        console.error(e);
        setError('Errore nel caricamento delle impostazioni.');
      })
      .finally(() => setLoading(false));
  }, [isAdmin, authLoading]);

  // Wait for auth context to restore session before redirecting
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/admin/login" replace />;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setSaved(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await axios.put(`${API}/settings`, form);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.detail || 'Errore durante il salvataggio.');
    } finally {
      setSaving(false);
    }
  };

  const startEditSumup = (event) => {
    setEditingEventId(event.id);
    setEditingSumupValue(event.sumup_url || '');
  };

  const cancelEditSumup = () => {
    setEditingEventId(null);
    setEditingSumupValue('');
  };

  const saveSumupForEvent = async (event) => {
    try {
      // Preserve all event fields; just update sumup_url
      const payload = { ...event, sumup_url: editingSumupValue || null };
      delete payload.id;
      delete payload.created_at;
      await axios.put(`${API}/events/${event.id}`, payload);
      setEvents((prev) =>
        prev.map((ev) => (ev.id === event.id ? { ...ev, sumup_url: editingSumupValue || null } : ev))
      );
      cancelEditSumup();
    } catch (err) {
      console.error(err);
      alert("Errore durante l'aggiornamento del link SumUp.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Group events by country for cleaner UI
  const countryNames = {
    italy: '🇮🇹 Italia',
    tunisia: '🇹🇳 Tunisia',
    libya: '🇱🇾 Libia',
    greece: '🇬🇷 Grecia',
    iceland: '🇮🇸 Islanda',
    colombia: '🇨🇴 Colombia',
  };
  const upcoming = events.filter((e) => e.status === 'upcoming');
  const grouped = upcoming.reduce((acc, ev) => {
    const key = ev.country || 'other';
    (acc[key] = acc[key] || []).push(ev);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="admin-payments-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/admin/dashboard" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-4">
          <ArrowLeft className="mr-2" size={20} />
          Torna alla Dashboard
        </Link>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Impostazioni Pagamento</h1>
        <p className="text-gray-600 mb-8">
          Gestisci gli IBAN, l&apos;intestatario e i link SumUp mostrati nei moduli di iscrizione.
        </p>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg" data-testid="payments-error">
            {error}
          </div>
        )}
        {saved && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg" data-testid="payments-saved">
            ✓ Impostazioni salvate. Le modifiche sono già visibili sul sito.
          </div>
        )}

        {/* IBAN & Holder */}
        <form onSubmit={handleSave} className="bg-white rounded-xl shadow p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-1 flex items-center">
            <Banknote className="mr-2 text-green-600" size={22} />
            Bonifico bancario
          </h2>
          <p className="text-sm text-gray-600 mb-6">
            Lascia un IBAN vuoto per non mostrarlo agli iscritti. Usa l&apos;etichetta per indicare la banca, es. &quot;IBAN Revolut (Lithuania)&quot;.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Etichetta IBAN 1</label>
              <input
                type="text" name="iban_1_label" value={form.iban_1_label} onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="IBAN Revolut (Lithuania)"
                data-testid="iban-1-label-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Numero IBAN 1</label>
              <input
                type="text" name="iban_1_number" value={form.iban_1_number} onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono"
                placeholder="LT77..."
                data-testid="iban-1-number-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Etichetta IBAN 2 (opzionale)</label>
              <input
                type="text" name="iban_2_label" value={form.iban_2_label} onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="IBAN DSK Bank (Bulgaria)"
                data-testid="iban-2-label-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Numero IBAN 2 (opzionale)</label>
              <input
                type="text" name="iban_2_number" value={form.iban_2_number} onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono"
                placeholder="BG32..."
                data-testid="iban-2-number-input"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Intestatario</label>
              <input
                type="text" name="iban_holder" value={form.iban_holder} onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="UGO FILOSA"
                data-testid="iban-holder-input"
              />
            </div>
          </div>

          <h2 className="text-xl font-bold text-gray-900 mt-8 mb-1 flex items-center">
            <CreditCard className="mr-2 text-blue-600" size={22} />
            Link SumUp predefinito
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Questo link viene usato quando un evento <strong>non ha</strong> un proprio link SumUp specifico. Lo puoi personalizzare per ogni evento qui sotto.
          </p>
          <input
            type="url" name="default_sumup_url" value={form.default_sumup_url} onChange={handleChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="https://pay.sumup.com/b2c/XXXXXXX"
            data-testid="default-sumup-input"
          />

          <div className="flex items-center justify-end mt-6">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:bg-gray-400"
              data-testid="save-payments-btn"
            >
              <Save className="mr-2" size={18} />
              {saving ? 'Salvataggio...' : 'Salva impostazioni'}
            </button>
          </div>
        </form>

        {/* Per-event SumUp links */}
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-1 flex items-center">
            <CreditCard className="mr-2 text-amber-600" size={22} />
            Link SumUp per singolo evento
          </h2>
          <p className="text-sm text-gray-600 mb-4 flex items-start">
            <Info size={16} className="mr-2 mt-0.5 text-amber-600 shrink-0" />
            Imposta un link diverso per ogni evento (es. quote acconto variabili: €500 Tunisia, €300 Italia, ecc.). Lascia vuoto per usare il link predefinito.
          </p>

          {upcoming.length === 0 ? (
            <p className="text-gray-500 py-8 text-center">Nessun evento in programma.</p>
          ) : (
            Object.keys(grouped).sort().map((country) => (
              <div key={country} className="mb-6">
                <h3 className="text-base font-semibold text-gray-800 mb-3">{countryNames[country] || country}</h3>
                <div className="space-y-2">
                  {grouped[country].map((ev) => (
                    <div key={ev.id} className="border border-gray-200 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`event-sumup-row-${ev.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{ev.title_it}</p>
                        <p className="text-xs text-gray-500">
                          {ev.date_start} → {ev.date_end}
                          {ev.deposit_amount > 0 && (
                            <span className="ml-2 font-semibold text-amber-700">
                              · Acconto € {Number(ev.deposit_amount).toLocaleString('it-IT')}
                            </span>
                          )}
                        </p>
                      </div>

                      {editingEventId === ev.id ? (
                        <div className="flex flex-col sm:flex-row gap-2 sm:items-center flex-1">
                          <input
                            type="url"
                            value={editingSumupValue}
                            onChange={(e) => setEditingSumupValue(e.target.value)}
                            placeholder="https://pay.sumup.com/..."
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                            autoFocus
                            data-testid={`event-sumup-input-${ev.id}`}
                          />
                          <button
                            type="button" onClick={() => saveSumupForEvent(ev)}
                            className="bg-green-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-green-700"
                            data-testid={`save-sumup-${ev.id}`}
                          >
                            Salva
                          </button>
                          <button
                            type="button" onClick={cancelEditSumup}
                            className="bg-gray-200 text-gray-700 px-3 py-2 rounded-lg text-sm hover:bg-gray-300"
                          >
                            Annulla
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 sm:flex-1">
                          <code className={`flex-1 truncate text-xs px-3 py-2 rounded ${ev.sumup_url ? 'bg-blue-50 text-blue-800' : 'bg-gray-50 text-gray-500 italic'}`}>
                            {ev.sumup_url || '(usa link predefinito)'}
                          </code>
                          <button
                            type="button" onClick={() => startEditSumup(ev)}
                            className="text-sm text-blue-600 hover:text-blue-800 font-medium whitespace-nowrap"
                            data-testid={`edit-sumup-${ev.id}`}
                          >
                            Modifica
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPayments;
